/**
 * 公開ページの原稿 (content/<種類>/<slug>.md) を読む共通の部品。
 * 練習問題 (problems.ts) と面接問題 (interview-questions.ts) が使う。
 *
 * 原稿の形は 2 つだけ
 * - 頭の --- で囲んだ欄。1 行 1 欄で、並びの欄だけ [a, b] と書く
 * - 本文は ## 見出し {#id} の節を決まった順で並べる。### は節の中の小見出し
 *
 * 文字列を受け取って形を確かめるだけにして、vite を通さずに試験から呼べるようにする
 */

export type MdSubsection = { id: string; heading: string; body: string };

export type MdSection<Id extends string = string> = {
  id: Id;
  heading: string;
  /** 最初の ### より前の本文 */
  body: string;
  items: MdSubsection[];
};

const HEADING_ID = /^(.*?)\s*\{#([a-z][a-z0-9-]*)\}\s*$/;

export const SLUG_PATTERN = /^[a-z][a-z0-9-]*$/;

export function parseFrontMatter(raw: string, source: string) {
  const match = /^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/.exec(raw);
  if (!match) throw new Error(`${source} に front matter がありません`);
  const fields = new Map<string, string>();
  for (const line of match[1].split(/\r?\n/)) {
    if (!line.trim()) continue;
    const field = /^([A-Za-z]+):\s*(.*)$/.exec(line);
    if (!field) throw new Error(`${source} の front matter の行が読めません (${line})`);
    if (fields.has(field[1])) throw new Error(`${source} の ${field[1]} が 2 回あります`);
    fields.set(field[1], field[2].trim());
  }
  return { fields, body: raw.slice(match[0].length) };
}

/** 欠けた欄があれば投げ、あれば中身を返す */
export function requireFields<Name extends string>(
  fields: Map<string, string>,
  names: readonly Name[],
  source: string,
): Record<Name, string> {
  const out = {} as Record<Name, string>;
  for (const name of names) {
    const value = fields.get(name);
    if (!value) throw new Error(`${source} に ${name} がありません`);
    out[name] = value;
  }
  return out;
}

/** [a, b] の形の欄を並びにする */
export function parseList(value: string, name: string, source: string) {
  const inner = /^\[(.*)\]$/.exec(value);
  if (!inner) throw new Error(`${source} の ${name} は [a, b] の形で書きます`);
  return inner[1]
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function parseDate(value: string, name: string, source: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error(`${source} の ${name} は YYYY-MM-DD で書きます`);
  }
  return value;
}

/** 節を決まった順で読む。順が違う、id が無い、最初の ## より前に本文がある、のどれでも投げる */
export function parseSections<Id extends string>(
  body: string,
  source: string,
  sectionIds: readonly Id[],
): MdSection<Id>[] {
  const sections: MdSection<Id>[] = [];
  let current: MdSection<Id> | null = null;
  let item: MdSubsection | null = null;
  let inFence = false;

  for (const line of body.split(/\r?\n/)) {
    if (/^```/.test(line)) inFence = !inFence;
    if (!inFence && line.startsWith("## ")) {
      const heading = HEADING_ID.exec(line.slice(3));
      if (!heading || !(sectionIds as readonly string[]).includes(heading[2])) {
        throw new Error(`${source} の節の見出しに決まった id がありません (${line})`);
      }
      current = { id: heading[2] as Id, heading: heading[1], body: "", items: [] };
      item = null;
      sections.push(current);
      continue;
    }
    if (!inFence && line.startsWith("### ") && current) {
      const text = line.slice(4);
      const heading = HEADING_ID.exec(text);
      item = {
        id: heading ? heading[2] : `${current.id}-${current.items.length + 1}`,
        heading: heading ? heading[1] : text.trim(),
        body: "",
      };
      current.items.push(item);
      continue;
    }
    if (item) item.body += `${line}\n`;
    else if (current) current.body += `${line}\n`;
    else if (line.trim()) throw new Error(`${source} の最初の ## より前に本文があります`);
  }

  for (const section of sections) {
    section.body = section.body.trim();
    for (const sub of section.items) sub.body = sub.body.trim();
  }

  const order = sections.map((section) => section.id).join(",");
  if (order !== sectionIds.join(",")) {
    throw new Error(`${source} の節の並びが決まりと違います (${order})`);
  }
  return sections;
}

/** 置き場のパスから slug を取る (content/interview/max-in-list.md なら max-in-list) */
export function slugFromPath(path: string) {
  return path.split("/").pop()!.replace(/\.md$/, "");
}

/** 本文の中の ```lang の囲みを、上から順に取り出す */
export function codeFences(markdown: string) {
  const fences: { lang: string; code: string }[] = [];
  const re = /^```([A-Za-z0-9_-]*)[^\n]*\n([\s\S]*?)^```\s*$/gm;
  for (let m = re.exec(markdown); m; m = re.exec(markdown)) {
    fences.push({ lang: m[1], code: m[2] });
  }
  return fences;
}

/**
 * 地の文だけを残す。コードの囲み、`コード`、リンクの行き先、見出しの {#id}、front matter を外す。
 * 半角コロンの検査に使う
 */
export function proseOnly(raw: string) {
  return raw
    .replace(/^---\n[\s\S]*?\n---\n/, "")
    .replace(/^```[\s\S]*?^```/gm, "")
    .replace(/`[^`\n]*`/g, "")
    .replace(/\]\([^)]*\)/g, "]")
    .replace(/\{#[a-z0-9-]+\}/g, "");
}

/** 前後のページ。索引と同じ順 (order) でたどる */
export function neighbors<T extends { slug: string }>(list: T[], slug: string) {
  const index = list.findIndex((item) => item.slug === slug);
  return {
    prev: index > 0 ? list[index - 1] : null,
    next: index >= 0 && index < list.length - 1 ? list[index + 1] : null,
  };
}
